import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import {
    AttendeeLite,
    Customer,
    TokenPair,
    UpsertCustomerPayload,
    VerifyOtpResponse,
} from './customer-auth.types';

@Injectable({
    providedIn: 'root',
})
export class CustomerAuthService {
    private readonly ACCESS_KEY = 'customerAccessToken';
    private readonly REFRESH_KEY = 'customerRefreshToken';

    /** Email entered on the login screen (kept across the OTP step). */
    public pendingEmail: string | null = null;
    public attendees: BehaviorSubject<AttendeeLite[]> = new BehaviorSubject<
        AttendeeLite[]
    >([]);
    public customer: BehaviorSubject<Customer | null> =
        new BehaviorSubject<Customer | null>(null);

    constructor(private httpClient: HttpClient) {}

    // ----- token storage -----
    get accessToken(): string {
        return localStorage.getItem(this.ACCESS_KEY) ?? '';
    }
    set accessToken(token: string) {
        localStorage.setItem(this.ACCESS_KEY, token);
    }
    get refreshToken(): string {
        return localStorage.getItem(this.REFRESH_KEY) ?? '';
    }
    set refreshToken(token: string) {
        localStorage.setItem(this.REFRESH_KEY, token);
    }

    private setTokens(pair: TokenPair): void {
        this.accessToken = pair.accessToken;
        this.refreshToken = pair.refreshToken;
    }

    /**
     * ยังมี session ที่ใช้ได้อยู่ไหม — ตัดสินจาก refresh token (อายุ 7 วัน),
     * ไม่ใช่ access token (อายุ 15 นาที). access token ที่หมดอายุปล่อยให้
     * interceptor refresh ให้เองตอนยิง API; ถ้า refresh ไม่ผ่าน interceptor
     * จะ signOut + ดีดไปหน้า login เอง.
     */
    isAuthenticated(): boolean {
        const token = this.refreshToken;
        return !!token && !AuthUtils.isTokenExpired(token);
    }

    signOut(): void {
        localStorage.removeItem(this.ACCESS_KEY);
        localStorage.removeItem(this.REFRESH_KEY);
        this.pendingEmail = null;
        this.attendees.next([]);
        this.customer.next(null);
    }

    private get authHeaders(): { [key: string]: string } {
        return { 'x-api-key': environment.apiKey };
    }

    requestOtp(email: string): Observable<{ sent: boolean }> {
        const normalized = email.trim().toLowerCase();
        return this.httpClient
            .post<{ result: { sent: boolean } }>(
                `${environment.baseApi}/v1/customer-auth/request-otp`,
                { email: normalized },
                { headers: this.authHeaders }
            )
            .pipe(
                map((res) => res.result),
                tap(() => {
                    this.pendingEmail = normalized;
                })
            );
    }

    verifyOtp(email: string, otp: string): Observable<VerifyOtpResponse> {
        const normalized = email.trim().toLowerCase();
        return this.httpClient
            .post<{ result: VerifyOtpResponse }>(
                `${environment.baseApi}/v1/customer-auth/verify-otp`,
                { email: normalized, otp },
                { headers: this.authHeaders }
            )
            .pipe(
                map((res) => res.result),
                tap((res) => {
                    this.setTokens(res);
                    this.pendingEmail = normalized;
                    this.attendees.next(res.attendees ?? []);
                    this.customer.next(res.customer ?? null);
                })
            );
    }

    refresh(): Observable<TokenPair> {
        return this.httpClient
            .post<{ result: TokenPair }>(
                `${environment.baseApi}/v1/customer-auth/refresh`,
                { refreshToken: this.refreshToken },
                { headers: this.authHeaders }
            )
            .pipe(
                map((res) => res.result),
                tap((pair) => this.setTokens(pair))
            );
    }

    getMe(): Observable<Customer | null> {
        return this.httpClient
            .get<{ result: Customer | null }>(
                `${environment.baseApi}/v1/customer-auth/me`,
                { headers: this.authHeaders }
            )
            .pipe(
                map((res) => res.result),
                tap((c) => this.customer.next(c))
            );
    }

    updateMe(payload: UpsertCustomerPayload): Observable<Customer> {
        return this.httpClient
            .patch<{ result: Customer }>(
                `${environment.baseApi}/v1/customer-auth/me`,
                payload,
                { headers: this.authHeaders }
            )
            .pipe(
                map((res) => res.result),
                tap((c) => this.customer.next(c))
            );
    }

    listAttendees(): Observable<AttendeeLite[]> {
        return this.httpClient
            .get<{ result: AttendeeLite[] }>(
                `${environment.baseApi}/v1/customer-auth/my-attendees`,
                { headers: this.authHeaders }
            )
            .pipe(
                map((res) => res.result),
                tap((a) => this.attendees.next(a ?? []))
            );
    }

    /**
     * Extract the real backend error code. The API's HttpExceptionFilter wraps
     * every error so the top-level messageCode is always "ERROR" and the real
     * code (e.g. OTP_SENT_RECENTLY) is nested one level deeper at error.error.
     */
    extractErrorCode(err: any): string {
        const code =
            err?.error?.error?.messageCode ?? err?.error?.messageCode;
        return code && code !== 'ERROR' ? code : 'UNKNOWN';
    }
}
